import {
  useListSupportActions,
  useListSupportSessions,
  type SupportSessionOut,
} from '@myrentalmate/api-client';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { okData, okItems } from '../../lib/apiData';
import { formatDateTime } from '../../lib/format';
import { TablePager, useTablePage } from '../../components/TablePager';

function ActionsDialog({ session, onClose }: { session: SupportSessionOut; onClose: () => void }) {
  const actions = okData(useListSupportActions(session.id).data)?.items ?? [];
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>What Happened in This Session</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {session.org_name}. {session.reason}
        </Typography>
        <Stack spacing={1}>
          {actions.map((action) => (
            <Stack key={action.id} direction="row" sx={{ justifyContent: 'space-between', gap: 2 }}>
              <Typography>
                {action.action} ({action.entity_type} {action.entity_id})
              </Typography>
              <Typography color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {formatDateTime(action.created_at)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Every time a platform admin opened a customer's records, and what they changed. */
export function SupportLogPage() {
  const paging = useTablePage();
  const query = useListSupportSessions(paging.params);
  const sessions = okItems(query.data);
  const [viewing, setViewing] = useState<SupportSessionOut | null>(null);
  const columns: DataColumn<SupportSessionOut>[] = [
    { label: 'Customer', render: (s) => s.org_name },
    { label: 'Superadmin', render: (s) => s.admin_email },
    { label: 'Reason', render: (s) => <Box sx={{ overflowWrap: 'anywhere' }}>{s.reason}</Box> },
    {
      label: 'Started',
      render: (s) => <Box sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(s.started_at)}</Box>,
    },
    {
      label: 'Status',
      render: (s) => (
        <Chip
          size="small"
          label={s.is_open ? 'Open' : 'Closed'}
          color={s.is_open ? 'warning' : 'default'}
        />
      ),
    },
    {
      label: 'Actions',
      align: 'right',
      render: (s) => (
        <Button size="small" onClick={() => setViewing(s)}>
          View
        </Button>
      ),
    },
  ];
  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="h5">Support Log</Typography>
        <Typography color="text.secondary">
          Every time a superadmin opened a customer's records.
        </Typography>
      </div>
      {sessions.length === 0 ? (
        <Typography color="text.secondary">No support sessions yet.</Typography>
      ) : (
        <>
          <DataTable columns={columns} rows={sessions} rowKey={(s) => s.id} />
          <TablePager paging={paging} total={okData(query.data)?.total ?? 0} />
        </>
      )}
      {viewing && <ActionsDialog session={viewing} onClose={() => setViewing(null)} />}
    </Stack>
  );
}
